import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsFetching } from '@tanstack/react-query';
import * as amplitude from '@amplitude/analytics-browser';

import {
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownMenuItem,
  FormatCurrencyForDisplay,
  getLocalizedCurrency,
} from '@unified-checkout/design-system';

import type { IManageAccountsProps } from '@app/dashboard/types/innerLayout/balance_and_transfers/balance/manage-accounts';
import { useLangStore } from '@app/dashboard/store/lang';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import {
  BALANCE_TRANSFERS_EVENTS,
  BALANCE_TRANSFERS_EVENTS_PROPERTIES,
} from '@app/dashboard/constants/tracking_events/balance-and-transfers';
import { SHARED_EVENTS_PARAMS } from '@app/dashboard/constants/tracking_events/shared';
import { getCurrentPath } from '@app/dashboard/utils/getCurrentPath';

import {
  formatAmountWithCurrency,
  getCurrencyFlag,
} from '@app/dashboard/views/pages/inner_layout/BalanceAndTransfers/utils';

import styles from './manage-accounts.module.scss';

const ManageAccounts = ({
  currencies,
  isLoading,
  onChangeActiveBalance,
  activeBalance,
}: IManageAccountsProps) => {
  const { t } = useTranslation();
  const { lang } = useLangStore();

  const isFetchingRecentTransfers = useIsFetching({
    queryKey: [QUERY_KEYS.GET_TRANSFERS],
  });

  const formattedAmount = (amount: number, currency: string) => {
    const formattedAmount = formatAmountWithCurrency({
      amount,
      currency,
      lang,
    });

    return FormatCurrencyForDisplay(formattedAmount);
  };

  const handleTrackOpenDropdown = (currency: string) => {
    const bankAccountCount =
      currencies?.find((c) => c.currency === currency)?.accounts?.length || 0;

    amplitude.track(
      BALANCE_TRANSFERS_EVENTS.BALANCE_PAGE.CURRENCY_DROPDOWN_OPENED,
      {
        [BALANCE_TRANSFERS_EVENTS_PROPERTIES.SHARED.SELECTED_CURRENCY]:
          currency,
        [BALANCE_TRANSFERS_EVENTS_PROPERTIES.BALANCE_PAGE.BANK_ACCOUNT_COUNT]:
          bankAccountCount,
        [SHARED_EVENTS_PARAMS.PAGE]: getCurrentPath(),
      }
    );
  };

  const renderMenuItem = ({
    icon,
    formattedCurrency,
    account_number,
  }: {
    icon: ReactNode;
    formattedCurrency: string | ReactNode;
    account_number: string;
  }) => {
    return (
      <div className={styles['manage-accounts__dropdown-item']}>
        <div className={styles['manage-accounts__dropdown-item__icon']}>
          {icon}
        </div>
        <div className={styles['manage-accounts__dropdown-item__text']}>
          <span
            className={styles['manage-accounts__dropdown-item__text__account']}
          >
            {formattedCurrency} |{' '}
            {t('innerLayout.balanceTransfers.balance.account')}
          </span>
          <span
            className={styles['manage-accounts__dropdown-item__text__number']}
          >
            {account_number}
          </span>
        </div>
      </div>
    );
  };

  if (!currencies?.length) return null;

  return (
    <div className={styles['manage-accounts']}>
      {currencies?.map(({ accounts, currency }) => {
        const selected = currency === activeBalance?.currency;
        const dropdownText = selected
          ? activeBalance?.account?.account_number
          : '';

        const icon = getCurrencyFlag(currency);
        const formattedCurrency = FormatCurrencyForDisplay(
          getLocalizedCurrency(lang, currency)
        );

        const label = selected
          ? ((
              <>
                {formattedCurrency} |{' '}
                {t('innerLayout.balanceTransfers.balance.account')}
              </>
            ) as any)
          : formattedCurrency;

        return (
          <Dropdown
            width="fit-content"
            key={currency}
            id={`${currency}-dropdown-selected-${
              currency === activeBalance?.currency
            }`}
          >
            <DropdownButton
              leadingIcon={icon}
              label={label}
              text={dropdownText}
              hideDropdownChevron={!selected}
              type="button"
              disabled={isLoading || !!isFetchingRecentTransfers}
              onClickHandler={() => handleTrackOpenDropdown(currency)}
            />
            <DropdownMenu width="fit-content">
              {accounts?.map((account) => {
                const { account_number, balance, id } = account || {};

                const selected = id === activeBalance?.account?.id;

                /* TODO: will uncomment this with (text, hintText) in phase 2 after solve the amount from BE */

                // const amount = formattedAmount(balance, currency);
                // const accountNumber = t(
                //   'innerLayout.balanceTransfers.balance.accountNumber',
                //   {
                //     account: account_number,
                //   }
                // );

                return (
                  <DropdownMenuItem
                    key={String(id)}
                    id={String(id)}
                    // text={amount as any}
                    // hintText={accountNumber}
                    selected={selected}
                    onClickHandler={() =>
                      onChangeActiveBalance(account, currency)
                    }
                    render={renderMenuItem({
                      icon,
                      formattedCurrency,
                      account_number,
                    })}
                  />
                );
              })}
            </DropdownMenu>
          </Dropdown>
        );
      })}
    </div>
  );
};

export default ManageAccounts;
